import type { ExternalIntegrationProvider, ExternalIntegrationStatus } from '@/data/models/domain/externalIntegrations'
import type { CustomFieldType } from '@/services/domain/cusomFields/types'
import { isCustomFieldTypeWithOptions } from '@/services/domain/cusomFields/types'
import type { TranslationKey } from '@/services/types'
import type {
  HubSpotPropertyFilterType,
  CrmPropertyWatchCondition,
  ZohoPropertyFieldType,
} from '@/data/models/domain/automations/types'
import type { TmAxiosResponse } from '@/services/transport/types'

export type HubspotExternalIntegrationSettings = {
  logOutboundSms: boolean
  logInboundSms: boolean
  logSmsOptOut: boolean
}

export type ShopifyExternalIntegrationSettings = {
  abandonedCartDelay: AbandonedCartDelay
}

export type WooCommerceExternalIntegrationSettings = {
  abandonedCartDelay: AbandonedCartDelay
}

export type ZohoExternalIntegrationSettings = Record<string, never>

export type ExternalIntegrationSettings =
  | HubspotExternalIntegrationSettings
  | ZohoExternalIntegrationSettings
  | ShopifyExternalIntegrationSettings
  | WooCommerceExternalIntegrationSettings

export type ExternalIntegrationSettingsResponse = {
  hubSpotSettings?: HubspotExternalIntegrationSettings | null
  shopifySettings?: ShopifyExternalIntegrationSettings | null
  wooCommerceSettings?: WooCommerceExternalIntegrationSettings | null
  zohoCrmSettings?: ZohoExternalIntegrationSettings | null
}

export type ExternalIntegrationResponse = {
  id: number
  provider: ExternalIntegrationProvider
  status: ExternalIntegrationStatus
  externalAccountId: string | null
  accountName: string | null
  manageUrl: string | null
  syncedCount: number | null
  failedCount: number | null
  hasError: boolean
  createdAt: string
  updatedAt: string
  settings?: ExternalIntegrationSettingsResponse | null
}

export type ExternalIntegrationDynamicFieldItem = {
  entity: string
  title: string
  fields: { name: string; title: string }[]
}

export type ConnectionRequest = {
  provider: ExternalIntegrationProvider
  storeName: string
  connectionId?: number
}

export type ExternalIntegrationCategoriesRequest = {
  connectionId: string
  searchText?: string
  parentId?: string
}

export interface IExternalIntegrationCrmService<TType = unknown> {
  getConnectionUrl(connectionId?: number | null): Promise<string>
  getFieldMapping(id: string): Promise<ExternalIntegrationFieldMappingResponse>
  saveFieldMapping(id: string, mapping: ExternalIntegrationMappingItemPayload[]): Promise<void>
  getContactRoutingRules(id: string): Promise<CrmContactRoutingRulesResponse>
  startSync(id: string, request: CrmStartSyncRequest): Promise<TmAxiosResponse<CrmStartSyncResponse>>
  getContactProperties(connectionId: string, searchText?: string): Promise<CrmPropertyResponse<TType>[]>
  getDealPipelines(connectionId: string): Promise<CrmPipelineResponse[]>
  getTicketPipelines(connectionId: string): Promise<CrmPipelineResponse[]>
  updateTimelineLogging(id: string, payload: CrmTimelineLoggingPayload): Promise<void>
}

export type ShopifyConnectionRequest = Omit<ConnectionRequest, 'provider'>

export type ShopifyCategoriesRequest = ExternalIntegrationCategoriesRequest

export type WooCommerceConnectionRequest = Omit<ConnectionRequest, 'provider'>

export type WooCommerceField = ExternalIntegrationField

export type WooCommerceMappingItem = ExternalIntegrationMappingItem

export type WooCommerceMappingItemPayload = ExternalIntegrationMappingItemPayload

export type WooCommerceFieldMappingResponse = ExternalIntegrationFieldMappingResponse

export type WooCommerceStartSyncRequest = {
  listId: number
}

export type WooCommerceStartSyncResponse = {
  customersCount: number
}

export type HubspotConnectionRequest = Omit<ConnectionRequest, 'provider'>

export type HubspotMappingItemPayload = ExternalIntegrationMappingItemPayload

export type HubspotFieldMappingResponse = ExternalIntegrationFieldMappingResponse

export type CrmContactRoutingRule = {
  conditions: CrmPropertyWatchCondition[]
  listId: number | null
  doNotImport: boolean
}

export type CrmStartSyncRequest = {
  rules: CrmContactRoutingRule[]
  fallbackListId: number | null
  doNotImportOtherContacts: boolean
}

export type CrmContactRoutingRulesResponse = {
  rules: CrmContactRoutingRule[]
  fallbackListId: number | null
  doNotImportOtherContacts: boolean
}

export type CrmStartSyncResponse = {
  customersCount: number
}

export type CrmTimelineLoggingPayload = {
  logOutboundSms: boolean
  logInboundSms: boolean
}

export type HubSpotPropertyOptionResponse = {
  value: string
  label: string
}

export type CrmPropertyResponse<TType> = {
  name: string
  label: string
  type: TType
  options: HubSpotPropertyOptionResponse[]
}

export type CrmPropertyListResponse<T> = {
  items: T[]
}

export type HubSpotPropertyResponse = CrmPropertyResponse<HubSpotPropertyFilterType>

export type HubSpotPropertyListResponse = CrmPropertyListResponse<HubSpotPropertyResponse>

export type ZohoPropertyResponse = CrmPropertyResponse<ZohoPropertyFieldType>

export type ZohoPropertyListResponse = CrmPropertyListResponse<ZohoPropertyResponse>

export type CrmOwnerResponse = {
  id: string
  label: string
  email: string
  type: string
}

export type HubSpotOwnerListResponse = {
  items: CrmOwnerResponse[]
}

export type HubSpotContactListResponse = {
  id: string
  name: string
}

export type HubSpotContactListsResponse = {
  items: HubSpotContactListResponse[]
}

type CrmPipelineStageResponse = {
  id: string
  label: string
}

export type CrmPipelineResponse = {
  id: string
  label: string
  stages: CrmPipelineStageResponse[]
}

export type HubSpotPipelineListResponse = {
  items: CrmPipelineResponse[]
}

export type HubSpotInboxResponse = {
  id: string
  label: string
}

export type HubSpotInboxListResponse = {
  items: HubSpotInboxResponse[]
}

export type HubSpotEntityPropertySource = 'contact' | 'deal' | 'ticket' | 'conversation' | 'task' | 'order' | 'meeting'

export type ExternalIntegrationFieldIdentity = 'email' | 'phone'

export type ExternalIntegrationField = {
  key: string
  title: string
  type: CustomFieldType
  identity?: ExternalIntegrationFieldIdentity | null
}

export type ShopifyField = ExternalIntegrationField

export const getMappedContactFieldType = (providerField: ExternalIntegrationField): CustomFieldType => {
  if (isCustomFieldTypeWithOptions(providerField.type)) {
    return 'text'
  }

  return providerField.type
}

export type ExternalIntegrationMappingItem = {
  provider: string
  tm: string
  // Backend tri-state: true = locked default field (not mappable), false = default identity field
  // (mappable, shows the "Default" badge), undefined = regular field.
  isDefault?: boolean
}

export type ExternalIntegrationMappingItemPayload = {
  provider: string
  tm: string
}

export type ExternalIntegrationFieldMappingResponse = {
  providerFields: ExternalIntegrationField[]
  contactFields: ExternalIntegrationField[]
  mapping: ExternalIntegrationMappingItem[]
}

export type ShopifyMappingItem = ExternalIntegrationMappingItem

export type ShopifyMappingItemPayload = ExternalIntegrationMappingItemPayload

export type ShopifyFieldMappingResponse = ExternalIntegrationFieldMappingResponse

export type ExternalIntegrationCategoryAncestorResponse = {
  id: string
  name: string
}

export type ExternalIntegrationCategoryResponse = {
  id: string
  title: string
  hasChildren: boolean
  childrenCount: number
  ancestors: ExternalIntegrationCategoryAncestorResponse[]
}

export type ExternalIntegrationCategoriesResponse = {
  items: ExternalIntegrationCategoryResponse[]
}

export type ExternalIntegrationProductsRequest = {
  connectionId: string
  searchText?: string
}

export type ExternalIntegrationProductResponse = {
  id: string
  title: string
  categoryId: string | null
  imageUrl: string | null
}

export type ExternalIntegrationProductsResponse = {
  items: ExternalIntegrationProductResponse[]
}

export type ShopifyCategoryAncestorResponse = ExternalIntegrationCategoryAncestorResponse

export type ShopifyCategoryResponse = ExternalIntegrationCategoryResponse

export type ShopifyCategoriesResponse = ExternalIntegrationCategoriesResponse

export type ShopifyProductsRequest = ExternalIntegrationProductsRequest

export type ShopifyProductResponse = ExternalIntegrationProductResponse

export type ShopifyProductsResponse = ExternalIntegrationProductsResponse

export type WooCommerceCategoriesRequest = ExternalIntegrationCategoriesRequest

export type WooCommerceCategoryAncestorResponse = ExternalIntegrationCategoryAncestorResponse

export type WooCommerceCategoryResponse = ExternalIntegrationCategoryResponse

export type WooCommerceCategoriesResponse = ExternalIntegrationCategoriesResponse

export type WooCommerceProductsRequest = ExternalIntegrationProductsRequest

export type WooCommerceProductResponse = ExternalIntegrationProductResponse

export type WooCommerceProductsResponse = ExternalIntegrationProductsResponse

export type WooCommerceCustomFieldsResponse = {
  order: string[]
  customer: string[]
}

export type ShopifyStartSyncResponse = {
  customersCount: number
}

export type ShopifyStartSyncRequest = {
  listId: number
}

export enum AbandonedCartDelay {
  MINUTES_10 = 600,
  MINUTES_30 = 1800,
  HOUR_1 = 3600,
  HOURS_2 = 7200,
  HOURS_4 = 14400,
  HOURS_6 = 21600,
  HOURS_12 = 43200,
  HOURS_24 = 86400,
  HOURS_48 = 172800,
}

export const ABANDONED_CART_DELAY_DEFAULT = AbandonedCartDelay.HOUR_1

export const abandonedCartDelayLabelMap: Record<AbandonedCartDelay, TranslationKey> = {
  [AbandonedCartDelay.MINUTES_10]: 'externalIntegrations.details.settings.abandonedCartDelay.options.minutes10',
  [AbandonedCartDelay.MINUTES_30]: 'externalIntegrations.details.settings.abandonedCartDelay.options.minutes30',
  [AbandonedCartDelay.HOUR_1]: 'externalIntegrations.details.settings.abandonedCartDelay.options.hour1',
  [AbandonedCartDelay.HOURS_2]: 'externalIntegrations.details.settings.abandonedCartDelay.options.hours2',
  [AbandonedCartDelay.HOURS_4]: 'externalIntegrations.details.settings.abandonedCartDelay.options.hours4',
  [AbandonedCartDelay.HOURS_6]: 'externalIntegrations.details.settings.abandonedCartDelay.options.hours6',
  [AbandonedCartDelay.HOURS_12]: 'externalIntegrations.details.settings.abandonedCartDelay.options.hours12',
  [AbandonedCartDelay.HOURS_24]: 'externalIntegrations.details.settings.abandonedCartDelay.options.hours24',
  [AbandonedCartDelay.HOURS_48]: 'externalIntegrations.details.settings.abandonedCartDelay.options.hours48',
}

export type SettingsIntegrationsFilterFilterName = 'categories'

export enum SettingsIntegrationsCategory {
  CRM = 'CRM',
  ECommerce = 'eCommerce',
  Automation = 'Automation',
  Other = 'Other',
}

type ShopifyTriggerVariable = `shopify.${string}.${string}`
type ShopifyEventVariable = `shopify.${number}.${string}.${string}`
export type ShopifyVariable = ShopifyTriggerVariable | ShopifyEventVariable

export enum ShopifyDynamicField {
  OrderName = 'shopify.order.name',
  OrderNumber = 'shopify.order.number',
  OrderEmail = 'shopify.order.email',
  OrderContactEmail = 'shopify.order.contact_email',
  OrderPhone = 'shopify.order.phone',
  OrderFinancialStatus = 'shopify.order.financial_status',
  OrderFulfillmentStatus = 'shopify.order.fulfillment_status',
  OrderTags = 'shopify.order.tags',
  OrderNote = 'shopify.order.note',
  OrderCreatedAt = 'shopify.order.created_at',
  OrderProcessedAt = 'shopify.order.processed_at',
  OrderUpdatedAt = 'shopify.order.updated_at',
  OrderClosedAt = 'shopify.order.closed_at',
  EstimatedDeliveryDate = 'shopify.edd.to',
  PricingTotalPrice = 'shopify.pricing.total_price',
  PricingSubtotalPrice = 'shopify.pricing.subtotal_price',
  PricingTotalShippingPrice = 'shopify.pricing.total_shipping_price',
  PricingTotalTax = 'shopify.pricing.total_tax',
  PricingTotalDiscounts = 'shopify.pricing.total_discounts',
  PricingCurrency = 'shopify.pricing.currency',
  PricingPresentmentCurrency = 'shopify.pricing.presentment_currency',
  CustomerFirstName = 'shopify.customer.first_name',
  CustomerLastName = 'shopify.customer.last_name',
  CustomerEmail = 'shopify.customer.email',
  CustomerPhone = 'shopify.customer.phone',
  CustomerState = 'shopify.customer.state',
  CustomerCurrency = 'shopify.customer.currency',
  CustomerId = 'shopify.customer.id',
  CustomerCountry = 'shopify.customer.country',
  CustomerCompanyName = 'shopify.customer.company_name',
  CustomerAddress1 = 'shopify.customer.address1',
  CustomerAddress2 = 'shopify.customer.address2',
  CustomerZip = 'shopify.customer.zip',
  CustomerEmailMarketingState = 'shopify.customer.email_marketing_state',
  CustomerEmailMarketingOptInLevel = 'shopify.customer.email_marketing_opt_in_level',
  CustomerEmailMarketingConsentUpdatedAt = 'shopify.customer.email_marketing_consent_updated_at',
  CustomerSmsMarketingState = 'shopify.customer.sms_marketing_state',
  CustomerSmsMarketingOptInLevel = 'shopify.customer.sms_marketing_opt_in_level',
  CustomerSmsMarketingConsentUpdatedAt = 'shopify.customer.sms_marketing_consent_updated_at',
  CustomerSmsMarketingConsentCollectedFrom = 'shopify.customer.sms_marketing_consent_collected_from',
  ShippingAddressFirstName = 'shopify.shipping_address.first_name',
  ShippingAddressLastName = 'shopify.shipping_address.last_name',
  ShippingAddressCompany = 'shopify.shipping_address.company',
  ShippingAddressAddress1 = 'shopify.shipping_address.address1',
  ShippingAddressAddress2 = 'shopify.shipping_address.address2',
  ShippingAddressCity = 'shopify.shipping_address.city',
  ShippingAddressProvince = 'shopify.shipping_address.province',
  ShippingAddressCountry = 'shopify.shipping_address.country',
  ShippingAddressZip = 'shopify.shipping_address.zip',
  ShippingAddressPhone = 'shopify.shipping_address.phone',
  ShippingAddressCountryCode = 'shopify.shipping_address.country_code',
  ShippingAddressProvinceCode = 'shopify.shipping_address.province_code',
  BillingAddressFirstName = 'shopify.billing_address.first_name',
  BillingAddressLastName = 'shopify.billing_address.last_name',
  BillingAddressCompany = 'shopify.billing_address.company',
  BillingAddressAddress1 = 'shopify.billing_address.address1',
  BillingAddressAddress2 = 'shopify.billing_address.address2',
  BillingAddressCity = 'shopify.billing_address.city',
  BillingAddressProvince = 'shopify.billing_address.province',
  BillingAddressCountry = 'shopify.billing_address.country',
  BillingAddressZip = 'shopify.billing_address.zip',
  BillingAddressPhone = 'shopify.billing_address.phone',
  BillingAddressCountryCode = 'shopify.billing_address.country_code',
  BillingAddressProvinceCode = 'shopify.billing_address.province_code',
  ShippingMethodTitle = 'shopify.shipping_method.title',
  ShippingMethodPrice = 'shopify.shipping_method.price',
  LineItemsSummary = 'shopify.line_items.summary',
  StoreName = 'shopify.store.name',
  StoreDomain = 'shopify.store.domain',
  StoreUrl = 'shopify.store.url',
  TrackingCompany = 'shopify.tracking.company',
  TrackingNumber = 'shopify.tracking.number',
  TrackingNumbers = 'shopify.tracking.numbers',
  TrackingUrl = 'shopify.tracking.url',
  TrackingUrls = 'shopify.tracking.urls',
  FulfillmentStatus = 'shopify.tracking.fulfillment_status',
  ShipmentStatus = 'shopify.tracking.shipment_status',
  CheckoutToken = 'shopify.checkout.token',
  CheckoutCartToken = 'shopify.checkout.cart_token',
  CheckoutAbandonedUrl = 'shopify.checkout.abandoned_url',
  CheckoutGateway = 'shopify.checkout.gateway',
  CheckoutCustomerLocale = 'shopify.checkout.customer_locale',
  CheckoutLandingSite = 'shopify.checkout.landing_site',
  CheckoutReferringSite = 'shopify.checkout.referring_site',
  CheckoutSourceName = 'shopify.checkout.source_name',
  CheckoutBuyerAcceptsMarketing = 'shopify.checkout.buyer_accepts_marketing',
  CheckoutBuyerAcceptsSmsMarketing = 'shopify.checkout.buyer_accepts_sms_marketing',
  CheckoutTotalLineItemsPrice = 'shopify.checkout.total_line_items_price',
  CheckoutTotalDuties = 'shopify.checkout.total_duties',
}

type WooCommerceTriggerVariable = `woocommerce.${string}.${string}`
type WooCommerceEventVariable = `woocommerce.${number}.${string}.${string}`
export type WooCommerceVariable = WooCommerceTriggerVariable | WooCommerceEventVariable

export enum WooCommerceDynamicField {
  OrderId = 'woocommerce.order.id',
  OrderNumber = 'woocommerce.order.number',
  OrderStatus = 'woocommerce.order.status',
  OrderCustomerNote = 'woocommerce.order.customer_note',
  OrderPaymentMethod = 'woocommerce.order.payment_method',
  OrderTransactionId = 'woocommerce.order.transaction_id',
  OrderCreatedAt = 'woocommerce.order.created_at',
  OrderModifiedAt = 'woocommerce.order.modified_at',
  OrderPaidAt = 'woocommerce.order.paid_at',
  OrderCompletedAt = 'woocommerce.order.completed_at',
  LineItemsSummary = 'woocommerce.line_items.summary',
  PricingTotal = 'woocommerce.pricing.total',
  PricingDiscountTotal = 'woocommerce.pricing.discount_total',
  PricingShippingTotal = 'woocommerce.pricing.shipping_total',
  PricingTotalTax = 'woocommerce.pricing.total_tax',
  PricingCurrency = 'woocommerce.pricing.currency',
  PricingCurrencySymbol = 'woocommerce.pricing.currency_symbol',
  CustomerId = 'woocommerce.customer.id',
  CustomerFirstName = 'woocommerce.customer.first_name',
  CustomerLastName = 'woocommerce.customer.last_name',
  CustomerEmail = 'woocommerce.customer.email',
  CustomerPhone = 'woocommerce.customer.phone',
  ShippingAddressFirstName = 'woocommerce.shipping_address.first_name',
  ShippingAddressLastName = 'woocommerce.shipping_address.last_name',
  ShippingAddressCompany = 'woocommerce.shipping_address.company',
  ShippingAddressAddress1 = 'woocommerce.shipping_address.address1',
  ShippingAddressAddress2 = 'woocommerce.shipping_address.address2',
  ShippingAddressCity = 'woocommerce.shipping_address.city',
  ShippingAddressState = 'woocommerce.shipping_address.state',
  ShippingAddressPostcode = 'woocommerce.shipping_address.postcode',
  ShippingAddressCountry = 'woocommerce.shipping_address.country',
  ShippingAddressPhone = 'woocommerce.shipping_address.phone',
  BillingAddressFirstName = 'woocommerce.billing_address.first_name',
  BillingAddressLastName = 'woocommerce.billing_address.last_name',
  BillingAddressCompany = 'woocommerce.billing_address.company',
  BillingAddressAddress1 = 'woocommerce.billing_address.address1',
  BillingAddressAddress2 = 'woocommerce.billing_address.address2',
  BillingAddressCity = 'woocommerce.billing_address.city',
  BillingAddressState = 'woocommerce.billing_address.state',
  BillingAddressPostcode = 'woocommerce.billing_address.postcode',
  BillingAddressCountry = 'woocommerce.billing_address.country',
  BillingAddressEmail = 'woocommerce.billing_address.email',
  BillingAddressPhone = 'woocommerce.billing_address.phone',
  ShippingMethodTitle = 'woocommerce.shipping_method.title',
  ShippingMethodTotal = 'woocommerce.shipping_method.total',
  StoreName = 'woocommerce.store.name',
  StoreUrl = 'woocommerce.store.url',
  CheckoutCustomerNote = 'woocommerce.checkout.customer_note',
  CheckoutCreatedAt = 'woocommerce.checkout.created_at',
  CheckoutModifiedAt = 'woocommerce.checkout.modified_at',
  CheckoutPaymentUrl = 'woocommerce.checkout.payment_url',
  CheckoutPaymentMethod = 'woocommerce.checkout.payment_method',
}

export enum WooCommerceCouponDynamicField {
  CouponId = 'woocommerce.coupon_id',
  CouponCode = 'woocommerce.coupon_code',
  CouponAmount = 'woocommerce.coupon_amount',
  CouponDiscountType = 'woocommerce.coupon_discount_type',
  CouponExpiresAt = 'woocommerce.coupon_expires_at',
  CouponUsageLimit = 'woocommerce.coupon_usage_limit',
}
